import { useFragment } from "react-relay"
import { useOutletContext } from "react-router"
import { CaretRight, PencilSimple } from "@phosphor-icons/react"
import { t } from "i18next"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardBodyContent, CardBodyHeader, CardBodyHeaderTitle, CardHeader } from "@/components/ui/card"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { cn } from "@/lib/utils"
import { Allergy } from "@/modules/patients/detail/health-information/allergy/allergy"
import { BodyMeasurement } from "@/modules/patients/detail/health-information/body-measurement/body-measurement"
import { Condition } from "@/modules/patients/detail/health-information/condition/condition"
import { FamilyHistory } from "@/modules/patients/detail/health-information/family-history/family-history"
import { Gynaecology } from "@/modules/patients/detail/health-information/gynaecology/gynaecology"
import { HealthInformationCopilotButton } from "@/modules/patients/detail/health-information/health-information-copilot-button"
import { Medication } from "@/modules/patients/detail/health-information/medication/medication"
import { SocialHistory } from "@/modules/patients/detail/health-information/social-history/social-history"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { PatientContextAppointmentFragment$key } from "@/modules/patients/services/__generated__/PatientContextAppointmentFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientContextAppointmentFragment } from "@/modules/patients/services/PatientContextAppointmentFragment"
import { Country } from "@/types/__generated__/union-types"

interface HealthDataCardSessionProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  sectionName: string
  analyticsSectionName: string
  editHandler?: () => void
  hasDetail?: boolean
  disabled?: boolean
  canVerify?: boolean
  bodyContentClassName?: string
}

const HealthDataCardSession = ({
  sectionName,
  analyticsSectionName,
  disabled,
  title,
  editHandler,
  children,
  canVerify = false,
  bodyContentClassName,
}: HealthDataCardSessionProps) => {
  const isEditMode = useEditMode()

  return (
    <>
      <CardBodyHeader className="pr-2">
        <CardBodyHeaderTitle
          className="text-on-surface-subdued self-center"
          data-testid={`${sectionName}-section-title`}>
          {title}
        </CardBodyHeaderTitle>
        <Button
          variant="circle"
          className="relative h-10 w-10"
          onClick={() => {
            if (editHandler) {
              FaroInsightsWrapper.pushEvent(`${analyticsSectionName}_edit`)
              editHandler()
            }
          }}
          disabled={disabled}
          data-testid={`${sectionName}-edit-btn`}>
          {isEditMode && (
            <>
              <PencilSimple className={disabled ? "text-on-surface-disabled" : "text-on-surface-subdued"} />
              {canVerify && (
                <span
                  data-testid={`${sectionName}-red-dot-icon`}
                  className="bg-critical absolute top-2 right-2 h-1.5 w-1.5 rounded-md"></span>
              )}
            </>
          )}
          {!isEditMode && <CaretRight className={disabled ? "text-on-surface-disabled" : "text-on-surface-subdued"} />}
        </Button>
      </CardBodyHeader>
      {children && (
        <CardBodyContent
          className={cn("empty:hidden", bodyContentClassName)}
          testId={title.toLowerCase().replace(/\s/g, "-")}>
          {children}
        </CardBodyContent>
      )}
    </>
  )
}

interface HealthInformationCardProps extends React.HTMLAttributes<HTMLDivElement> {
  country: Country
  patientId: string
}
const HealthInformationCard = ({ country, patientId }: HealthInformationCardProps) => {
  const { patient, contextAppointment, isEditMode } = useOutletContext<{
    patient: PatientFragment$data
    contextAppointment: PatientContextAppointmentFragment$key | null
    isEditMode: boolean
  }>()
  const contextAppointmentData = useFragment(PatientContextAppointmentFragment, contextAppointment)

  return (
    <ScrollWrapper className="block! grid-rows-1 py-8 pr-4">
      <Card className="animate-fade-in-bottom">
        <CardHeader data-testid="health-card-header" className="flex items-center justify-between truncate pr-4">
          <div className="text-on-surface text-base leading-6 font-medium">{t("patient.healthInformation.title")}</div>
          {contextAppointmentData?.chatDetails?.chatThreadId && isEditMode && <HealthInformationCopilotButton />}
        </CardHeader>
        <CardBody data-testid="health-card-body">
          <BodyMeasurement country={country} patientId={patientId} />
          <Condition country={country} patientId={patientId} />
          {(patient.gender === "FEMALE" || patient.gender === "OTHER") && patient.age && patient.age.years >= 11 && (
            <Gynaecology country={country} patientId={patientId} />
          )}
          <Medication patientId={patientId} />
          <Allergy country={country} patientId={patientId} />
          <SocialHistory country={country} patientId={patientId} />
          <FamilyHistory country={country} patientId={patientId} />
        </CardBody>
      </Card>
    </ScrollWrapper>
  )
}

export { HealthDataCardSession, HealthInformationCard }
